import React from 'react'
import { NarrativeSectionKey, SprintPacketHydrated } from '@marketing-milk/interfaces'
import { radius, SurfacePalette, type } from '../../../tokens'
import { useSectionNumber } from '../../PacketSectionsContext'
import { MarketingMilkHandoffCard } from '../../sections/MarketingMilkHandoffCard'
import { PresentPage } from '../PresentPage'

interface Props {
  palette: SurfacePalette
  packet: SprintPacketHydrated
}

const BUDGET_ROWS: Array<{ sectionKey: NarrativeSectionKey; label: string }> = [
  { sectionKey: 'budget-current', label: 'Current monthly budget' },
  { sectionKey: 'budget-target', label: 'Target-ROI budget' },
  { sectionKey: 'budget-increase', label: 'Suggested monthly increase' },
  { sectionKey: 'budget-projected', label: 'Projected results' },
]

export const PresentPerformance = ({ palette, packet }: Props) => {
  const number = useSectionNumber('performance-review')
  const narrative = packet.narrative ?? []
  const hasBudget = BUDGET_ROWS.some(
    r => (narrative.find(b => b.sectionKey === r.sectionKey)?.richText ?? '').trim() !== ''
  )

  return (
    <PresentPage
      palette={palette}
      number={number}
      title="Performance Review"
      commentAnchor={{
        type: 'section',
        id: `${packet.id}:performance-review`,
        label: 'Performance Review',
      }}
    >
      <MarketingMilkHandoffCard palette={palette} packet={packet} />

      <div style={{ marginTop: 32 }}>
        <div
          style={{
            fontFamily: type.family.serif,
            fontSize: type.size.h1,
            fontWeight: type.weight.regular,
            color: palette.text,
            marginBottom: 16,
          }}
        >
          Budget Recommendation
        </div>
        {hasBudget ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 720 }}>
            {BUDGET_ROWS.map(r => {
              const value = (
                narrative.find(b => b.sectionKey === r.sectionKey)?.richText ?? ''
              ).trim()
              if (!value) return null
              return (
                <div
                  key={r.sectionKey}
                  style={{
                    padding: '14px 16px',
                    border: `1px solid ${palette.hairline}`,
                    borderRadius: radius.md,
                    background: palette.surface,
                  }}
                >
                  <div
                    style={{
                      fontFamily: type.family.mono,
                      fontSize: type.size.eyebrow,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: palette.textMuted,
                      marginBottom: 6,
                    }}
                  >
                    {r.label}
                  </div>
                  <div
                    style={{
                      fontFamily: type.family.sans,
                      fontSize: type.size.bodyLg,
                      color: palette.text,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {value}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div
            style={{
              fontFamily: type.family.sans,
              fontSize: type.size.bodyLg,
              fontStyle: 'italic',
              color: palette.textFaint,
            }}
          >
            No budget recommendation captured.
          </div>
        )}
      </div>
    </PresentPage>
  )
}
